import { useState, useEffect, useRef } from 'react';
import { 
  Palette, 
  PenLine, 
  Type,
  ChevronDown,
  X
} from 'lucide-react';

// Color presets for fill and stroke
const colorPresets = [
  { color: '#1E293B', name: 'Dark' },
  { color: '#64748B', name: 'Gray' },
  { color: '#EF4444', name: 'Red' },
  { color: '#F97316', name: 'Orange' },
  { color: '#EAB308', name: 'Yellow' },
  { color: '#22C55E', name: 'Green' },
  { color: '#3B82F6', name: 'Blue' },
  { color: '#6366F1', name: 'Indigo' },
  { color: '#8B5CF6', name: 'Purple' },
  { color: '#EC4899', name: 'Pink' },
  { color: '#FFFFFF', name: 'White' },
  { color: 'transparent', name: 'None' },
];

const strokeWidths = [1, 2, 3, 4, 6];

export const SelectionPropertiesPanel = ({
  selectedObjects,
  onUpdateObjects,
  position,
}) => {
  const [activeTab, setActiveTab] = useState(null);
  const panelRef = useRef(null);

  // Determine what types of objects are selected
  const hasShapes = selectedObjects.some(obj => obj.type === 'shape');
  const hasStickies = selectedObjects.some(obj => obj.type === 'sticky');
  const hasText = selectedObjects.some(obj => obj.type === 'text');
  const hasDrawings = selectedObjects.some(obj => obj.type === 'drawing');
  
  // Get current values from first selected object (for display)
  const firstObj = selectedObjects[0];
  const currentFill = firstObj?.fill || firstObj?.color || '#FFFFFF';
  const currentStroke = firstObj?.stroke || '#1E293B';
  const currentStrokeWidth = firstObj?.strokeWidth || 2;
  const currentTextColor = firstObj?.textColor || firstObj?.color || '#1E293B';

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setActiveTab(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!selectedObjects.length) return null;

  // Only show for shapes, stickies, text, and drawings
  if (!hasShapes && !hasStickies && !hasText && !hasDrawings) return null;

  const handleFillChange = (color) => {
    const updates = {};
    if (hasShapes) updates.fill = color;
    if (hasStickies) updates.color = color;
    onUpdateObjects(selectedObjects.map(obj => obj.id), updates);
  };

  const handleStrokeChange = (color) => {
    onUpdateObjects(selectedObjects.map(obj => obj.id), { stroke: color });
  };

  const handleStrokeWidthChange = (width) => {
    onUpdateObjects(selectedObjects.map(obj => obj.id), { strokeWidth: width });
  };

  const handleTextColorChange = (color) => {
    const updates = {};
    if (hasShapes || hasStickies) updates.textColor = color;
    if (hasText) updates.color = color;
    onUpdateObjects(selectedObjects.map(obj => obj.id), updates);
  };

  // Calculate panel position (above the selection)
  const panelStyle = {
    position: 'fixed',
    left: position.x,
    top: Math.max(60, position.y - 50), // Keep it below header
    transform: 'translateX(-50%)',
    zIndex: 60,
  };

  return (
    <div
      ref={panelRef}
      className="bg-white rounded-lg shadow-lg border border-gray-200 flex items-center gap-1 p-1"
      style={panelStyle}
      data-testid="selection-properties-panel"
    >
      {/* Fill Color (for shapes and stickies) */}
      {(hasShapes || hasStickies) && (
        <div className="relative">
          <button
            onClick={() => setActiveTab(activeTab === 'fill' ? null : 'fill')}
            className={`flex items-center gap-1 px-2 py-1.5 rounded hover:bg-gray-100 ${activeTab === 'fill' ? 'bg-gray-100' : ''}`}
            title="Fill Color"
            data-testid="fill-color-btn"
          >
            <div 
              className="w-4 h-4 rounded border border-gray-300"
              style={{ backgroundColor: currentFill === 'transparent' ? 'white' : currentFill }}
            >
              {currentFill === 'transparent' && (
                <div className="w-full h-full relative">
                  <div className="absolute inset-0 bg-[linear-gradient(45deg,#ccc_25%,transparent_25%,transparent_75%,#ccc_75%,#ccc),linear-gradient(45deg,#ccc_25%,transparent_25%,transparent_75%,#ccc_75%,#ccc)] bg-[length:8px_8px] bg-[position:0_0,4px_4px]" />
                </div>
              )}
            </div>
            <Palette size={14} className="text-gray-500" />
            <ChevronDown size={12} className="text-gray-400" />
          </button>
          
          {activeTab === 'fill' && (
            <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 p-2 min-w-[180px]">
              <div className="text-xs font-medium text-gray-500 mb-2">Fill Color</div>
              <div className="grid grid-cols-6 gap-1">
                {colorPresets.map((c) => (
                  <button
                    key={c.color}
                    className={`w-6 h-6 rounded border-2 transition-all ${
                      currentFill === c.color 
                        ? 'border-blue-500 scale-110' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={{ 
                      backgroundColor: c.color === 'transparent' ? 'white' : c.color,
                      backgroundImage: c.color === 'transparent' 
                        ? 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)'
                        : 'none',
                      backgroundSize: '8px 8px',
                      backgroundPosition: '0 0, 4px 4px'
                    }}
                    onClick={() => {
                      handleFillChange(c.color);
                      setActiveTab(null);
                    }}
                    title={c.name}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stroke Color (for shapes and drawings) */}
      {(hasShapes || hasDrawings) && (
        <div className="relative">
          <button
            onClick={() => setActiveTab(activeTab === 'stroke' ? null : 'stroke')}
            className={`flex items-center gap-1 px-2 py-1.5 rounded hover:bg-gray-100 ${activeTab === 'stroke' ? 'bg-gray-100' : ''}`}
            title="Stroke Color"
            data-testid="stroke-color-btn"
          >
            <div 
              className="w-4 h-4 rounded border-2"
              style={{ borderColor: currentStroke }}
            />
            <PenLine size={14} className="text-gray-500" />
            <ChevronDown size={12} className="text-gray-400" />
          </button>
          
          {activeTab === 'stroke' && (
            <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 p-2 min-w-[180px]">
              <div className="text-xs font-medium text-gray-500 mb-2">Stroke Color</div>
              <div className="grid grid-cols-6 gap-1 mb-3">
                {colorPresets.filter(c => c.color !== 'transparent').map((c) => (
                  <button
                    key={c.color}
                    className={`w-6 h-6 rounded border-2 transition-all ${
                      currentStroke === c.color 
                        ? 'border-blue-500 scale-110' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={{ backgroundColor: c.color }}
                    onClick={() => {
                      handleStrokeChange(c.color);
                    }}
                    title={c.name}
                  />
                ))}
              </div>
              
              <div className="text-xs font-medium text-gray-500 mb-2">Stroke Width</div>
              <div className="flex gap-1">
                {strokeWidths.map((w) => (
                  <button
                    key={w}
                    className={`flex-1 py-1 text-xs rounded ${
                      currentStrokeWidth === w 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    onClick={() => {
                      handleStrokeWidthChange(w);
                    }}
                  >
                    {w}px
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Text Color (for shapes with text, stickies, and text objects) */}
      {(hasShapes || hasStickies || hasText) && (
        <div className="relative">
          <button
            onClick={() => setActiveTab(activeTab === 'text' ? null : 'text')}
            className={`flex items-center gap-1 px-2 py-1.5 rounded hover:bg-gray-100 ${activeTab === 'text' ? 'bg-gray-100' : ''}`}
            title="Text Color"
            data-testid="text-color-btn"
          >
            <Type size={14} style={{ color: currentTextColor }} />
            <div 
              className="w-3 h-1 rounded-full"
              style={{ backgroundColor: currentTextColor }}
            />
            <ChevronDown size={12} className="text-gray-400" />
          </button>
          
          {activeTab === 'text' && (
            <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 p-2 min-w-[180px]">
              <div className="text-xs font-medium text-gray-500 mb-2">Text Color</div>
              <div className="grid grid-cols-6 gap-1">
                {colorPresets.filter(c => c.color !== 'transparent').map((c) => (
                  <button
                    key={c.color}
                    className={`w-6 h-6 rounded border-2 transition-all ${
                      currentTextColor === c.color 
                        ? 'border-blue-500 scale-110' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={{ backgroundColor: c.color }}
                    onClick={() => {
                      handleTextColorChange(c.color);
                      setActiveTab(null);
                    }}
                    title={c.name}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Close button */}
      <button
        onClick={() => setActiveTab(null)}
        className="p-1.5 rounded hover:bg-gray-100 text-gray-400 ml-1"
        title="Close"
      >
        <X size={14} />
      </button>
    </div>
  );
};
